import React, { createContext, useContext, useState, useEffect } from "react";
import { DEFAULT_CURRENCY, BASE_CURRENCIES } from "../config/currencyConfig";
import { fetchLatestRates } from "../services/currencyService";

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem("preferredCurrency");
    return saved && ["USD", "AED", "IQD"].includes(saved)
      ? saved
      : DEFAULT_CURRENCY;
  });

  const [rates, setRates] = useState({
    USD: 1,
    AED: 1,
    IQD: 1,
  });

  const [ratesLoaded, setRatesLoaded] = useState(false);

  // Persist currency
  useEffect(() => {
    localStorage.setItem("preferredCurrency", currency);
  }, [currency]);

  // Fetch backend rates
  useEffect(() => {
    const loadRates = async () => {
      try {
        console.log("🚀 Fetching rates from backend...");
        const result = await fetchLatestRates();
       
        if (result) {
          // Backend returns { aedRate, iqdRate } with USD as base (1)
          setRates({
            USD: 1,
            AED: Number(result.aedRate) || 1,
            IQD: Number(result.iqdRate) || 1,
          });
        }
      } catch (error) {
        console.error("❌ Failed to fetch rates:", error);
      } finally {
        setRatesLoaded(true);
      }
    };

    loadRates();
  }, []);

  const convertCurrency = (amount, fromCode, toCode) => {
    if (typeof amount !== "number" || isNaN(amount)) return 0;
    if (fromCode === toCode) return amount;

    // Convert via USD as base
    const inUSD = amount / rates[fromCode];
    return inUSD * rates[toCode];
  };

  const formatCurrency = (amount, code) => {
    const currencyMeta = BASE_CURRENCIES[code];

    return amount.toLocaleString("en-US", {
      minimumFractionDigits: currencyMeta.decimalPlaces,
      maximumFractionDigits: currencyMeta.decimalPlaces,
    });
  };

  if (!ratesLoaded) {
    return null; // or spinner
  }

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        changeCurrency: setCurrency,
        rates,
        convertCurrency,
        formatCurrency,
        currencies: BASE_CURRENCIES,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used within CurrencyProvider");
  }
  return context;
};